import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, Loader2, Shield, CreditCard, CheckCircle } from 'lucide-react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { checkoutService } from '@/services/checkout'
import styles from './AddPaymentMethod.module.css'

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '')

function PaymentForm({ onSuccess }: { onSuccess: () => void }) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return

    setSubmitting(true)
    setError(null)

    const { error: setupError } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    })

    if (setupError) {
      setError(setupError.message || 'Failed to save payment method')
      setSubmitting(false)
    } else {
      onSuccess()
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && (
        <p className="mt-3 text-sm text-theme-danger">{error}</p>
      )}
      <button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full mt-6 py-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-opacity bg-theme-primary text-theme-bg font-orbitron"
        style={{ opacity: submitting ? 0.6 : 1 }}
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            SAVING...
          </>
        ) : (
          'SAVE PAYMENT METHOD'
        )}
      </button>
    </form>
  )
}

export function AddPaymentMethod() {
  const navigate = useNavigate()
  const location = useLocation()
  const returnTo = (location.state as any)?.returnTo || '/dashboard'

  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    let cancelled = false
    const init = async () => {
      try {
        const { client_secret } = await checkoutService.createSetupIntent()
        if (!cancelled) {
          setClientSecret(client_secret)
          setLoading(false)
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || 'Failed to initialize payment setup')
          setLoading(false)
        }
      }
    }
    init()
    return () => { cancelled = true }
  }, [])

  const handleSuccess = async () => {
    setSuccess(true)
    // Wait for Stripe to propagate the payment method, then verify
    await new Promise(r => setTimeout(r, 1500))
    try {
      const b = await checkoutService.getBalance()
      if (!b.has_payment_method) {
        // Stripe hasn't propagated yet — wait a bit longer
        await new Promise(r => setTimeout(r, 2000))
      }
    } catch {
      // Continue anyway
    }
    navigate(returnTo, { replace: true })
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-bg">
        <div className="text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-theme-primary" />
          <span className="text-theme-secondary font-roboto-mono">INITIALIZING PAYMENT...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-bg">
        <div className="max-w-md mx-auto text-center rounded-xl p-8 border-2 bg-surface border-theme-danger">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 ${styles.errorIconBg}`}>
            <CreditCard className="w-8 h-8 text-theme-danger" />
          </div>
          <h2 className="text-xl font-bold mb-3 text-theme-danger font-orbitron">SETUP_ERROR</h2>
          <p className="text-sm mb-5 text-theme-text">{error}</p>
          <button
            onClick={() => navigate(returnTo, { replace: true })}
            className="w-full py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 border bg-secondary-glow text-theme-secondary border-theme-secondary"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-bg">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <CheckCircle className={`w-16 h-16 mx-auto mb-4 ${styles.successIcon}`} />
          <h2 className={`text-xl font-bold mb-2 font-orbitron ${styles.successTitle}`}>CARD SAVED</h2>
          <p className="text-sm text-theme-text">Redirecting you back...</p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-theme-bg">
      <div className="px-6 pt-6">
        <button
          onClick={() => navigate(returnTo, { replace: true })}
          className="flex items-center gap-2 text-sm transition-colors text-theme-secondary font-roboto-mono"
        >
          <ArrowLeft className="w-4 h-4" />
          BACK
        </button>
      </div>

      <div className="max-w-lg mx-auto px-6 py-8">
        <div className="rounded-xl p-6 border-2 bg-surface border-theme-secondary">
          <h2 className="text-lg font-bold mb-5 text-theme-primary font-orbitron">ADD PAYMENT METHOD</h2>

          {clientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <PaymentForm onSuccess={handleSuccess} />
            </Elements>
          )}

          <div className={`mt-6 pt-4 flex items-center gap-4 text-xs text-theme-text ${styles.footerDivider}`}>
            <div className="flex items-center gap-1">
              <Shield className="w-3.5 h-3.5" />
              <span>Secure setup</span>
            </div>
            <div className="flex items-center gap-1">
              <CreditCard className="w-3.5 h-3.5" />
              <span>Powered by Stripe</span>
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-xl p-5 border bg-surface border-theme-border">
          <h3 className="font-semibold mb-2 text-theme-secondary">Why add a payment method?</h3>
          <ul className="space-y-1 text-sm text-theme-text">
            <li>- Your card will be charged only if you go over the free limit</li>
            <li>- Credits are purchased automatically to cover your overage</li>
            <li>- You can update or remove your card at any time</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
